import { useQuery } from '@tanstack/react-query';
import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import type { Item } from 'src/lib/api/types/items';
import type {
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
} from 'src/lib/api/types/roadmapping';
import { useTimelineLayout } from '@/lib/queries/roadmapping/useTimelineLayout';
import { getVisualizationByTypeQueryOptions } from '@/lib/queries/roadmapping/useVisualization';
import { headerToPropertySelector } from '@/lib/utils/roadmapping/commonLayoutUtils';
import { useVisualizationColorData } from '../utils/visualization-colors';
import { useViewContext } from '../ViewContext';

export function useTimelineColorContext({
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  noValueLabel,
  isPublishedMode = false,
  publishedLayout,
}: {
  items: Item[];
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  noValueLabel: string;
  isPublishedMode?: boolean;
  publishedLayout?: TimelineLayoutDTO;
}) {
  const { viewId } = useViewContext();

  const { data: visualization } = useQuery({
    ...getVisualizationByTypeQueryOptions(viewId, 'timeline'),
    enabled: !isPublishedMode && !!viewId,
  });

  const visualizationId = isPublishedMode
    ? (publishedLayout?.visualizationId ?? 0)
    : (visualization?.visualizationId ?? 0);

  const { data: fetchedLayout } = useTimelineLayout(viewId, visualizationId, {
    enabled: !isPublishedMode,
  });

  const effectiveLayout = isPublishedMode ? publishedLayout : fetchedLayout;

  const effectiveHeaders = useMemo(() => {
    if (!effectiveLayout) return undefined;
    return {
      header1: headerToPropertySelector(effectiveLayout.header1),
      header2: headerToPropertySelector(effectiveLayout.header2),
      itemLevelId: effectiveLayout.itemLevelId,
    };
  }, [effectiveLayout]);

  const colorData = useVisualizationColorData({
    layout: effectiveLayout,
    headers: effectiveHeaders,
    items,
    itemTypes,
    itemLevels,
    itemStatuses,
    noValueLabel,
    columnHeaderOrder: undefined,
    rowHeaderOrder: undefined,
    enabled: !isPublishedMode,
  });

  return {
    viewId,
    visualizationId,
    layout: effectiveLayout,
    headers: effectiveHeaders,
    colorConfig: colorData.colorConfig,
    items: colorData.items,
    allItemsWithParents: colorData.allItemsWithParents,
    timelineHeader1: colorData.visualizationHeader1,
    timelineHeader2: colorData.visualizationHeader2,
    header1FieldId: colorData.header1FieldId,
    header2FieldId: colorData.header2FieldId,
    defaultColorByField: colorData.defaultColorByField,
    currentColorByField: colorData.currentColorByField,
    isBaseColorSelected: colorData.isBaseColorSelected,
    isColorByFieldAHeader: colorData.isColorByFieldAHeader,
    matchingHeader: colorData.matchingHeader,
    headerForColorField: colorData.headerForColorField,
    uniqueHeaderValues: colorData.uniqueHeaderValues,
    orderedHeaderValues: colorData.orderedHeaderValues,
    currentHeaderColorOrder: colorData.currentHeaderColorOrder,
    paletteSwatches: colorData.paletteSwatches,
    labelMap: colorData.labelMap,
    colorItems: colorData.colorItems,
  };
}
